import {
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import { provideNzDateFnsAdapter } from 'ng-zorro-antd/core/time';
import { enUS } from 'date-fns/locale';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import {
  provideFieldControls,
  provideFieldComponents,
} from './modules/components/fields/field-register.provider';

import { routes } from './app.routes';
import { DatePipe, DOCUMENT, registerLocaleData } from '@angular/common';
import { provideServiceWorker } from '@angular/service-worker';
import { authInterceptor } from './interceptors/auth.interceptor';
import { originIdInterceptor } from './interceptors/origin-id.interceptor';
import { environment } from '../environments/environment';
import { TimezoneService } from './services/timezone.service';
import { KeyboardInsetService } from './services/keyboard-inset.service';
import { provideTouchTapDelivery } from './services/touch-tap-delivery.provider';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import en from '@angular/common/locales/en';
import { Title } from '@angular/platform-browser';
import { RuntimeConfigService } from './services/config/runtime-config.service';
import { ProductProfileService } from './services/config/product-profile.service';
import { WIDGET_PALETTE } from './modules/dashboards/widgets/shared/widget-palette';
import { PartialHighchartsConfig, provideHighcharts } from 'highcharts-angular';

registerLocaleData(en);

export const loadHighchartsRootModules: NonNullable<
  PartialHighchartsConfig['modules']
> = () => [
  import('highcharts/esm/highcharts-more').then(
    () => import('highcharts/esm/modules/solid-gauge')
  ),
];

export function loadRuntimeConfigAndApplyProductChrome(
  runtimeConfigService: RuntimeConfigService,
  productProfileService: ProductProfileService,
  title: Title,
  documentRef: Document,
  timezoneService: TimezoneService
): () => Promise<void> {
  return async () => {
    await runtimeConfigService.load();

    const productProfile = productProfileService.current();
    title.setTitle(productProfile.name);
    documentRef.documentElement.setAttribute('data-product', productProfile.id);

    timezoneService.start();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideAngularSvgIcon(),
    provideHttpClient(
      withXhr(),
      withInterceptors([authInterceptor, originIdInterceptor])
    ),
    provideNzDateFnsAdapter({ locale: enUS, firstDayOfWeek: 1 }),
    DatePipe,
    provideServiceWorker('checklist-sw.js', {
      enabled: environment.serviceWorkerEnabled,
      registrationStrategy: 'registerWhenStable:10000',
    }),
    RuntimeConfigService,
    TimezoneService,
    {
      provide: APP_INITIALIZER,
      useFactory: loadRuntimeConfigAndApplyProductChrome,
      deps: [
        RuntimeConfigService,
        ProductProfileService,
        Title,
        DOCUMENT,
        TimezoneService,
      ],
      multi: true,
    },
    provideAppInitializer(() => {
      inject(KeyboardInsetService);
    }),
    provideTouchTapDelivery(),
    provideFieldComponents(),
    provideFieldControls(),
    provideNzI18n(en_US),
    provideHighcharts({
      modules: loadHighchartsRootModules,
      // Highcharts sizes SVG text in px, which ignores the reader's text-size setting, while rem follows the root.
      options: {
        chart: { style: { fontSize: '0.875rem' } },
        colors: [...WIDGET_PALETTE],
      },
    }),
  ],
};
